import { isEmpty } from "lodash";
import { ComponentProps, useMemo, useRef, useState } from "react";
import {
  CallListDataFragment,
  useGetCallContactListLazyQuery
} from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import { MultiGroupedSelect, sortSelectedFirst } from "@bonliva-crm/ui";
import { useUpdateEffect } from "@hooks";
import { useFormField } from "@clientUtils";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";

export type CallAndContactListFilterProps = FilterWithFavouriteProps;

export const CallAndContactListFilter = (props: CallAndContactListFilterProps) => {
  const title = props.label ?? locale.callAndContactLists;

  const [callLists, setCallLists] = useState<CallListDataFragment[]>([]);
  const [contactLists, setContactLists] = useState<CallListDataFragment[]>([]);

  const selectRef = useRef(null);

  const { field, fieldState, setFieldValue, resetField } = useFormField<LabelValue[]>(
    props.name
  );

  const [expanded, setExpanded] = useState(fieldState.isDirty);

  const [loadItems, callContactListQuery] = useGetCallContactListLazyQuery({
    fetchPolicy: "cache-and-network",
    onCompleted(data) {
      const _callLists = data.me.CallLists.filter(
        (list) => !list.deleted && list.nodes.length > 0
      ).sort((a, b) => a.name.localeCompare(b.name));

      const _contactLists = data.me.ContactLists.filter(
        (list) => !list.deleted && list.nodes.length > 0
      ).sort((a, b) => a.name.localeCompare(b.name));

      setCallLists(_callLists);
      setContactLists(_contactLists);
    }
  });

  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current.focus();
    }
  }, [expanded]);

  const inputOptions = useMemo(() => {
    const callListOptions = callLists.map((callList) => ({
      label: callList.name,
      value: callList._id
    }));

    const contactListOptions = contactLists.map((contactList) => ({
      label: contactList.name,
      value: contactList._id
    }));

    const inputOptions = [
      {
        label: locale.CallLists,
        options: callListOptions
      },
      {
        label: locale.ContactLists,
        options: contactListOptions
      }
    ];

    return inputOptions;
  }, [callLists, contactLists]);

  const onChange = (values: LabelValue[]) => {
    setFieldValue(values, { shouldDirty: true });
  };

  const commonProps: ComponentProps<typeof MultiGroupedSelect<LabelValue>> = {
    placeholder: title,
    options: inputOptions,
    name: props.name,
    value: field.value,
    onChange: onChange,
    onMenuOpen: () => {
      return loadItems();
    },
    isClearable: !isEmpty(field.value),
    maxMenuHeight: 600,
    controlShouldRenderValue: false,
    isLoading: callContactListQuery.loading,
    showOptionDivider: false,
    showDropDownIndicator: true,
    isMulti: true,
    showBadge: !isEmpty(field.value),
    sortOptionsOnMenuOpenFn: (options) =>
      sortSelectedFirst({
        options,
        value: field.value,
        valueOptionEqualityFn: (value, option) => value.value === option.value
      }),
    onClear: () => setFieldValue(null, { shouldDirty: true })
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={fieldState.isDirty}
        isClearable={fieldState.isDirty}
        isExpanded={expanded}
        onClear={() => resetField()}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <MultiGroupedSelect<LabelValue>
          {...commonProps}
          selectRef={selectRef}
          isClearable={false}
          inputLabel={null}
          openMenuOnFocus
          menuPortalTarget={document.body}
          controlShouldRenderValue
          placeholder={locale.SelectValue}
          showBadge={false}
          menuPlacement="auto"
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <MultiGroupedSelect
      {...commonProps}
      styleProps={{
        control: { showBorder: true },
        menu: { showBorder: true }
      }}
    />
  );
};
